import styled from 'styled-components';
import Button from '@components/shared/Button';
import Colors from '@components/shared/Colors';
import { P } from '@components/shared/Typography';
import { EXTERNAL_LINK_SVG, MEDIA } from '@constants';
import Arrow from '@images/account/arrow-sm-fgreen-up.svg';

interface IInputStyled extends Partial<HTMLInputElement> {
  $error?: string;
}

export const InputStyled = styled.input<IInputStyled>`
  font-family: 'BrownStd';
  font-size: 15px;
  line-height: 24px;
  background: ${Colors.cream};
  color: inherit;

  border-width: 1px;
  border-style: solid;
  border-radius: 2px;
  height: 50px;
  padding: 0 15px;
  margin-bottom: 0;
  margin-top: 10px;

  width: 100%;
  border-color: ${(props) => (props.$error ? Colors.red : Colors.gold)};

  &::placeholder {
    padding-top: 2px;
  }

  &[type='number'] {
    padding: 0 5px 0 20px;
  }
`;

export const ButtonStyled2 = styled(Button)`
  background: none;
  border-bottom: 2px solid ${Colors.navyBlue};
  color: ${Colors.navyBlue};
  padding: 4px 0;
  height: auto;
  margin-bottom: 20px;
  text-transform: none;
  font-family: BrownStd;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.4375px;
  text-align: left;
  border-radius: 0;

  &:hover {
    background: none;
    color: ${Colors.navyBlue};
    opacity: 0.5;
  }

  @media only screen and (max-width: 500px) {
    padding: 4px 0;
    height: auto;
    margin-top: 20px;
    width: fit-content;
  }
`;

export const ButtonStyled = styled(Button)`
  border: 1px solid #00391e;
  background-color: #fefcea;
  color: #00391e;
  font-size: 12px;
  padding: 0;
  height: 45px;
  width: 180px;
  &:hover {
    background: none;
    border: 2px solid #00391e;
  }
  @media only screen and (max-width: 500px) {
    margin-top: 20px;
    width: 100%;
  }
`;

export const ButtonStyledDrawer = styled(Button)`
  margin: 20px 0 0;
  background: #d16aa9;
  width: 100%;
  font-size: 12px;

  &:hover {
    background: ${Colors.navyBlue};
  }
  @media only screen and (max-width: 500px) {
    margin-top: 20px;
    width: 100%;
  }
`;

interface PStyledBoldProps {
  $past: boolean;
}

export const PStyledBold = styled(P)<PStyledBoldProps>`
  font-size: 14px;
  color: ${({ $past }) => ($past ? '#b5c0af' : '#00391e')};
`;

export const Left = styled.div`
  width: 60%;
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: 500px) {
    width: 100%;
  }
`;

export const Right = styled.div`
  @media only screen and (max-width: 500px) {
    width: 100%;
  }
`;

export const Row = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin: 0 0 40px;

  @media only screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

interface AccountTabInnerProps {
  $fullWidth: boolean;
  $mobileMargin?: boolean;
}

export const AccountTabInner = styled.div<AccountTabInnerProps>`
  max-width: ${({ $fullWidth }) => ($fullWidth ? '100%' : '580px')};
  width: 100%;
  margin-bottom: 100px;

  @media only screen and (max-width: 768px) {
    width: ${({ $mobileMargin }) => ($mobileMargin ? 'auto' : '100%')};
    margin: ${({ $mobileMargin }) => ($mobileMargin ? '0 30px 20px' : '0 auto 20px')};
    max-width: 100%;
  }
`;

export const SectionHeadingRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 20px;
  color: ${Colors.navyBlue};
`;

interface SectionHeadingProps {
  $drawer?: boolean;
}

export const SectionHeading = styled.h4<SectionHeadingProps>`
  font-family: BrownStd;
  font-size: ${({ $drawer }) => ($drawer ? '16px' : '18px')};
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0;
  text-align: left;
  color: ${Colors.navyBlue};
`;

export const SectionHeadingLink = styled.span<{ $spaceOnTop?: boolean }>`
  font-family: BrownStd;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.4375px;
  text-align: left;
  color: ${Colors.navyBlue};
  padding-bottom: 2px;
  border-bottom: 2px solid ${Colors.navyBlue};
  cursor: pointer;
  margin-left: 10px;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }
  @media (max-width: ${(p) => (p.$spaceOnTop ? 580 : 1)}px) {
    margin-top: 20px;
  }
`;

export const SectionText = styled.p`
  font-family: BrownStd;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0;
  text-align: left;
  margin-bottom: 20px;
  color: ${Colors.navyBlue};

  span,
  strong {
    display: block;
  }
`;

export const SectionColourText = styled.div`
  background: ${Colors.ecru50};
  border-radius: 2px;
  padding: 20px 20px;
  font-family: BrownStd;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0;
  text-align: left;

  p {
    font-family: BrownStd;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0;
    margin-bottom: 14px;
    color: ${Colors.navyBlue};
  }

  p.small {
    font-family: BrownStd;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 21px;
    letter-spacing: 0;
    text-align: left;
    margin: 0;
  }

  p.small-bold {
    font-family: BrownStd;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 21px;
    letter-spacing: 0;
    text-align: left;
    margin: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;

export const SectionActionRow = styled.div`
  border: 1px solid ${Colors.paleGold};
  box-sizing: border-box;
  border-radius: 2px;
  padding: 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

export const SectionActionRowAlt = styled(SectionActionRow)`
  background: ${Colors.ecru50};
  border-width: 0;
  margin-bottom: 1px;

  &:last-of-type {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

export const SectionActionText = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  flex-grow: 1;

  p {
    font-family: BrownStd;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: ${Colors.navyBlue};

    @media only screen and (max-width: 768px) {
      margin-bottom: 4px;
    }
  }

  span {
    font-family: BrownStd;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0;
    color: ${Colors.navyBlue};

    @media only screen and (max-width: 768px) {
      margin-bottom: 4px;
    }
  }

  small {
    font-family: BrownStd;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0;
    color: ${Colors.navyBlue};
  }
`;

export const SectionActionAux = styled.div`
  display: flex;
  flex-direction: row;

  @media only screen and (max-width: 768px) {
    align-items: flex-start;
    margin-top: 20px;
  }
`;

export const SectionActionButton = styled(Button)`
  background: ${Colors.paleGold};
  border-radius: 4px;
  color: ${Colors.navyBlue};
  font-size: 12px;
  font-family: BrownStd;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 1px;
  text-align: center;
  height: 50px;
  flex-shrink: 1;
  margin-left: 20px;
  padding: 2px 20px 0 20px;

  &:first-child {
    @media only screen and (max-width: 768px) {
      margin-left: 0;
    }
  }

  &:hover {
    background: ${Colors.navyBlue};
    color: ${Colors.paleGold};
  }
`;

export const SectionDivider = styled.div`
  background: ${Colors.ecru};
  width: 100%;
  height: 1px;
  margin: 30px 0;
`;

export const AddressList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  font-family: BrownStd;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: ${Colors.navyBlue};

  li {
    font-size: inherit;
    line-height: inherit;
    color: inherit;

    &:first-child {
      font-weight: 700;
    }
  }
`;

export const IconTextBlock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background: #fdf3de;
  border-radius: 8px;
  padding: 15px 20px;
  margin-bottom: 24px;

  p {
    font-family: BrownStd;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 21px;
    letter-spacing: 0;
    text-align: left;
    color: ${Colors.navyBlue};

    a {
      font-weight: bold;
      border-bottom: 2px solid ${Colors.navyBlue};

      &:hover {
        text-decoration: none;
        opacity: 0.5;
      }
    }
  }

  svg {
    margin-right: 20px;
    min-width: 20px;
    path {
      stroke: ${Colors.navyBlue};
    }
  }
`;

export const DrawerActionTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;

  > p {
    font-family: BrownStd;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0;
    color: ${Colors.navyBlue};
    text-align: start;
    padding: 20px 0px;

    span {
      display: block;
      font-weight: 700;
    }
  }

  > p + p {
    padding-top: 0;
  }

  > p.bold {
    font-size: 16px;
    line-height: 24px;
    font-weight: 700;
  }

  > p.error {
    font-weight: 700;
    color: #ea534f;
    margin: 20px;
    line-height: 150%;
  }
`;

export const CancelButtonWrap = styled.div`
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 60px;
`;

export const IntroductionWrap = styled.div`
  background: #e5f2e3;
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  margin-bottom: 30px;
`;

export const IntroductionToggleButton = styled.div`
  font-family: 'BrownStd';
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.4375px;
  text-align: left;
  padding: 15px 20px;
  cursor: pointer;
  color: ${Colors.navyBlue};
`;

interface ToggleButtonArrowStyledProps {
  $isOpen: boolean;
}

export const ToggleButtonArrowStyled = styled(Arrow)<ToggleButtonArrowStyledProps>`
  transform: ${(props) => (props.$isOpen ? 'none' : 'rotate(180deg)')};
  float: right;
  height: 22px;

  @media (max-width: 768px) {
    height: 7px;
  }
`;

interface IntroductionProps {
  $isOpen: boolean;
  $loaded: boolean;
  $height: number;
}

export const Introduction = styled.div<IntroductionProps>`
  display: flex;
  transition: ${({ $loaded }) => ($loaded ? 'height 0.4s ease-in-out' : 'none')};
  height: ${({ $isOpen, $height }) => ($isOpen ? `${$height}px` : 0)};
  overflow: hidden;
  border-top: 1px solid #fffbe7;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media only screen and (max-width: 768px) {
    text-align: left;
  }

  svg,
  img {
    height: auto;
    width: 156px;
    margin-bottom: 30px;
  }

  h5 {
    font-family: 'BrownStd';
    color: ${Colors.navyBlue};
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 27px;
    letter-spacing: 0;
    color: ${Colors.navyBlue};
    margin-bottom: 10px;

    @media only screen and (max-width: 768px) {
      font-size: 16px;
      line-height: 24px;
    }
  }

  p {
    font-family: BrownStd;
    color: ${Colors.navyBlue};
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    margin-bottom: 10px;
    max-width: 390px;

    @media only screen and (max-width: 768px) {
      font-size: 14px;
      line-height: 21px;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  p.bold {
    font-weight: 700;
  }
`;

export const IntroductionInner = styled.div`
  padding: 30px;
`;

export const FullWidthDivider = styled.div`
  border-top: 1px solid ${Colors.ecru};
  width: calc(100% + 120px) !important;
  margin: 40px -60px;

  @media (max-width: 768px) {
    width: calc(100% + 40px) !important;
    margin: 30px -20px;
  }
`;

export const StickyButtonContainer = styled.div`
  background: ${Colors.cream};
  padding: 20px 60px 20px;
  width: calc(100% + 120px) !important;
  margin-left: -60px;
  position: sticky;
  bottom: 0;
  z-index: 2;

  @media (max-width: 768px) {
    position: relative;
    width: 100% !important;
    margin: 0;
    padding: 20px 0;
  }
`;

export const ModernSliderWrapper = styled.div`
  padding-bottom: 25px;

  .flickity-page-dots {
    bottom: -60px !important;

    .dot {
      margin: 0;
      width: 65px;
      height: 4px;
      border-radius: 2px;
      background: ${Colors.navyBlue};
      opacity: 0.1;

      &.is-selected {
        opacity: 0.5;
        background: ${Colors.navyBlue};
      }
    }
  }

  .slick-slide {
    padding: 0 10px 0 0;
  }

  .slick-list {
    padding: 0 !important;
    margin: 0 -10px 0 0;
    overflow: visible;
  }

  .slick-dots {
    bottom: -32px;

    li {
      width: 60px;
      margin: 0px;

      button {
        &:before {
          border-radius: 0;
          left: 5px;
          width: 60px;
          height: 4px;
          opacity: 0.1;
          background: ${Colors.navyBlue} !important;
        }

        &:hover,
        &:focus {
          &:before {
            opacity: 0.5 !important;
          }
        }
      }

      &:first-child {
        button {
          &:before {
            border-radius: 2px 0 0 2px;
          }
        }
      }

      &:last-child {
        button {
          &:before {
            border-radius: 0 2px 2px 0;
          }
        }
      }

      &.slick-active {
        button {
          :before {
            opacity: 0.8 !important;
          }
        }
      }
    }
  }
`;

export const ScreeningTestInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0;
  gap: 20px;
  margin-top: -20px;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    margin-top: 0;
    margin-bottom: 10px;
  }
`;

export const ScreeningMetaContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0;
  gap: 20px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    margin-bottom: 30px;
  }
`;

export const SummaryStepsIcon = styled.div`
  display: flex;
  width: 40px;
  min-width: 40px;
  height: 40px;
  padding: 4px 0 0 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 20px;
  background: ${Colors.cream};
  color: ${Colors.navyBlue};
  text-align: center;
  font-family: BrownStd;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
  margin: -5px 0;

  @media (max-width: 768px) {
    margin: 0;
  }
`;

export const SummaryStepsCardContent = styled.div<{ $background?: string }>`
  background: ${({ $background }) => $background || Colors.cream};
  padding: 30px 30px;
  border: 1px solid ${Colors.mint};
  border-radius: 0 0 2px 2px;
  margin: -30px 0 -30px 0;

  > div {
    &:not(:last-child) {
      border-bottom: 1px solid ${Colors.mint};
      margin: 0 -30px;
      padding: 0 30px 30px 30px;
    }

    &:not(:first-child) {
      padding-top: 30px;
    }

    &:last-child {
      padding: 30px;
      margin: 0 -30px -30px -30px;
    }
  }

  @media (max-width: 768px) {
    padding: 20px 20px;
    margin: -20px 0 -20px 0;

    > div {
      &:not(:last-child) {
        margin: 0 -20px;
        padding: 0 20px 20px 20px;
      }

      &:not(:first-child) {
        padding-top: 20px;
      }

      &:last-child {
        padding: 20px;
        margin: 0 -20px -20px -20px;
      }
    }
  }
`;

export const StydyLinkWrapper = styled.div<{ $margin?: string }>`
  a {
    font-family: 'BrownStd';
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 140%;
    text-align: center;
    color: ${Colors.lilac};
    margin-top: 5px;
    margin: ${({ $margin }) => $margin || '0 5px 0 0'};

    &:hover {
      text-decoration: underline;
    }

    &:after {
      content: '';
      background-image: url(${EXTERNAL_LINK_SVG});
      background-repeat: no-repeat;
      background-position: center;
      width: 15px;
      height: 15px;
      display: inline-block;
      margin-left: 5px;
      vertical-align: -2px;
    }

    @media (max-width: 1150px) {
      text-align: left;
      align-items: flex-start;

      &:after {
        width: 14px;
        height: 14px;
      }
    }

    @media (max-width: 700px) {
      font-weight: 700;
      font-size: 13px;
      line-height: 160%;
    }
  }
`;

export const ParameterSummaryIcon = styled.div<{ $isSmaller?: boolean }>`
  margin-right: 10px;
  min-width: 30px;
  width: 30px;
  display: flex;
  align-items: start;
  justify-content: end;

  img {
    max-width: ${({ $isSmaller }) => ($isSmaller ? 25 : 30)}px;
  }
`;

export const VideoIcon = styled.div`
  width: 40px;
  display: flex;
  align-items: center;

  img {
    max-width: 40px;
  }
`;

export const HeadingWithRoundedBackground = styled.div<{ $background: string }>`
  border-radius: 0 0 128px 128px;
  background: ${(props) => props.$background || 'linear-gradient(180deg, #EBF5EA 0%, rgba(235, 245, 234, 0.30) 100%)'};
  padding: 0 60px 24px 60px;
  margin: -50px -60px 24px -60px;

  ${MEDIA.tablet} {
    margin: -30px -24px 24px -24px;
    padding: 0 24px 24px 24px;
  }
`;

export const FlickityDotsWrapper = styled.div`
  .flickity-page-dots {
    padding: 3px;
    background: ${Colors.cream};
    bottom: 10px !important;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 16px;
    width: fit-content;
  }

  .flickity-page-dot {
    width: 8px;
    height: 8px;
    margin: 2px;
  }
`;

export const NavigationButtonStyled = styled.button`
  width: 32px;
  height: 32px;
  background-color: ${Colors.cream};
  border: 2px solid ${Colors.paleGold};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: BrownStd;
  font-size: 24px;
  font-weight: 400;
  color: ${Colors.navyBlue};
  z-index: 10;
  transition: all 0.2s ease;

  > svg {
    path {
      stroke: ${Colors.navyBlue};
    }
  }

  &:hover {
    background-color: ${Colors.paleGold};
    transform: scale(1.1);
  }
`;
